import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Connection,
  type Edge,
  type FinalConnectionState,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './styles.css';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';

import { ConfirmModal } from '../components/ui/ConfirmModal';
import { MessageModal } from '../components/ui/MessageModal';
import * as api from './api';
import { Palette } from './components/Palette';
import { ConnectPicker } from './components/ConnectPicker';
import { useSession } from '../hooks/useSession';
import { PropertiesPanel } from './components/PropertiesPanel';
import { Topbar } from './components/Topbar';
import { ModuleNode, MODULE_NODE_WIDTH } from './components/ModuleNode';
import { StartNode } from './components/StartNode';
import {
  ElasticEdge,
  ElasticConnectionLine,
  freshEdgeIds,
  triggerEdgeBounce,
} from './components/ElasticEdge';
import { defaultKindFor } from './components/ParamEditor';
import { autoLayoutTopDown } from './autoLayout';
import { LookupProvider } from './LookupContext';
import type {
  FlowGraph,
  FlowGraphEdge,
  LayoutDocument,
  ModuleCatalog,
  ModuleDef,
  ModuleExit,
  ModuleNodeData,
  ModuleParameter,
  NodePosition,
} from './types';

const NODE_TYPES = { module: ModuleNode, start: StartNode };
const EDGE_TYPES = { elastic: ElasticEdge };

// Synthetic "Start" node that is drawn above the InitialNodeId so users can
// see at a glance where the flow begins. It is NOT persisted to the backend:
//  - `saveFlowLayout` strips it out
//  - click/drop/delete handlers ignore it
const START_NODE_ID = '__start__';
const START_EDGE_ID = '__start_edge__';

interface MountProps {
  applicationId: number;
  flowId: number;
  initialFlowName: string;
  /**
   * Optional callback for the topbar's "Back" button. The new portal routes
   * back to /applications by default; pass a custom handler to override.
   */
  onBack?: () => void;
}

type PendingFlowDialog =
  | null
  | {
      kind: "confirm";
      title: string;
      message: string;
      tone: "danger" | "caution";
      confirmLabel?: string;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "message";
      title: string;
      message: string;
      resolve: () => void;
    };

export default function FlowBuilderApp(props: MountProps) {
  return (
    <ReactFlowProvider>
      <LookupProvider applicationId={props.applicationId}>
        <FlowBuilderInner {...props} />
      </LookupProvider>
    </ReactFlowProvider>
  );
}

function FlowBuilderInner({
  applicationId,
  flowId,
  initialFlowName,
  onBack,
}: MountProps) {
  // Phase 2 of the aivrmanager port: editing is now live (no claim/commit, the
  // canvas writes straight back to the active flow). We keep the readOnly
  // flag in place because the surrounding handlers branch on it everywhere
  // and we may want to flip it back per-flow once we add a publish workflow.
  const readOnly = false;
  const [catalog, setCatalog] = useState<ModuleCatalog | null>(null);
  // Voicebot modules (category "ASR/STT/TTS") are offered only to an admin
  // impersonating the customer. Existing nodes of that kind still render
  // and can be edited: only the palette and the connect picker are filtered.
  const { session } = useSession();
  const canUseVoicebotModules = session?.originalKind === 'Admin';
  const offeredCatalog = useMemo<ModuleCatalog | null>(() => {
    if (!catalog || canUseVoicebotModules) return catalog;
    const hidden = new Set(
      catalog.categories.filter((c) => c.name.trim().toUpperCase() === 'ASR/STT/TTS').map((c) => c.categoryId),
    );
    if (hidden.size === 0) return catalog;
    return {
      ...catalog,
      categories: catalog.categories.filter((c) => !hidden.has(c.categoryId)),
      modules: catalog.modules.filter((m) => !hidden.has(m.categoryId)),
    };
  }, [catalog, canUseVoicebotModules]);
  const [graph, setGraph] = useState<FlowGraph | null>(null);
  const [nodes, setNodes] = useState<Node<ModuleNodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [flowName, setFlowName] = useState(initialFlowName);
  const [initialNodeId, setInitialNodeId] = useState<number | null>(null);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDialog, setPendingDialog] = useState<PendingFlowDialog>(null);

  const confirmAsync = useCallback(
    (
      title: string,
      message: string,
      opts?: { tone?: "danger" | "caution"; confirmLabel?: string },
    ) =>
      new Promise<boolean>((resolve) => {
        setPendingDialog({
          kind: "confirm",
          title,
          message,
          tone: opts?.tone ?? "danger",
          confirmLabel: opts?.confirmLabel,
          resolve,
        });
      }),
    [],
  );

  const messageAsync = useCallback(
    (title: string, message: string) =>
      new Promise<void>((resolve) => {
        setPendingDialog({ kind: "message", title, message, resolve });
      }),
    [],
  );

  const dismissConfirm = useCallback((result: boolean) => {
    setPendingDialog((p) => {
      if (p?.kind === "confirm") p.resolve(result);
      return null;
    });
  }, []);

  const dismissMessage = useCallback(() => {
    setPendingDialog((p) => {
      if (p?.kind === "message") p.resolve();
      return null;
    });
  }, []);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const rf = useReactFlow();

  // Layout state — palette collapse-to-rail + resizable properties panel.
  // Both are persisted in localStorage so a user's preferred working layout
  // survives page reloads and tab switches.
  const [paletteCollapsed, setPaletteCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem('fb.paletteCollapsed');
    // Phones start with the palette as a rail so the canvas is visible.
    if (stored == null) return window.matchMedia?.('(max-width: 1023px)').matches ?? false;
    return stored === '1';
  });
  const togglePalette = useCallback(() => {
    setPaletteCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem('fb.paletteCollapsed', next ? '1' : '0');
      } catch {
        /* swallow storage errors (private mode, quota, etc.) */
      }
      return next;
    });
  }, []);

  // Minimap: can be hidden (persisted); phones start without it.
  const [miniMapVisible, setMiniMapVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('fb.miniMap');
    if (stored == null) return !(window.matchMedia?.('(max-width: 1023px)').matches ?? false);
    return stored === '1';
  });
  const toggleMiniMap = useCallback(() => {
    setMiniMapVisible((v) => {
      const next = !v;
      try {
        window.localStorage.setItem('fb.miniMap', next ? '1' : '0');
      } catch {
        /* swallow storage errors */
      }
      return next;
    });
  }, []);

  // Minimap size follows the canvas aspect ratio (a phone canvas is tall,
  // so the map is too), measured with a ResizeObserver on the wrapper.
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setCanvasSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const miniMapWidth = canvasSize.w < 768 ? 140 : 200;
  const miniMapHeight = Math.round(Math.min(360, Math.max(60, (miniMapWidth * canvasSize.h) / canvasSize.w)));

  const PROPS_MIN = 280;
  const PROPS_MAX = 600;
  const PROPS_DEFAULT = 400;
  const [propsWidth, setPropsWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return PROPS_DEFAULT;
    const raw = window.localStorage.getItem('fb.propsWidth');
    const n = raw == null ? NaN : Number(raw);
    if (!Number.isFinite(n)) return PROPS_DEFAULT;
    return Math.min(PROPS_MAX, Math.max(PROPS_MIN, n));
  });

  // Drag-to-resize for the properties panel splitter. We track mouse moves on
  // window so the user can drag past the body element (otherwise the resize
  // would freeze whenever they overshoot the original handle position).
  const splitterDragging = useRef(false);
  const onSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    splitterDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);
  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!splitterDragging.current) return;
      // Properties panel sits at the right edge; width = distance from cursor
      // to viewport right edge.
      const next = window.innerWidth - e.clientX;
      const clamped = Math.min(PROPS_MAX, Math.max(PROPS_MIN, next));
      setPropsWidth(clamped);
    }
    function onUp() {
      if (!splitterDragging.current) return;
      splitterDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        window.localStorage.setItem('fb.propsWidth', String(propsWidth));
      } catch {
        /* swallow storage errors */
      }
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [propsWidth]);

  // Quick lookup: moduleId -> ModuleDef.
  const moduleMap = useMemo(() => {
    const map = new Map<number, ModuleDef>();
    if (catalog) for (const m of catalog.modules) map.set(m.moduleId, m);
    return map;
  }, [catalog]);

  // Initial load: catalog + graph.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, gr] = await Promise.all([
          api.fetchModuleCatalog(),
          api.fetchFlowGraph(applicationId, flowId),
        ]);
        if (cancelled) return;
        setCatalog(cat);
        setGraph(gr);
        setFlowName(gr.flowName || initialFlowName);
        setInitialNodeId(gr.initialNodeId);
        const layout = parseLayout(gr.layoutJson);
        const { rfNodes, rfEdges } = hydrate(gr, cat, layout);
        setNodes(rfNodes);
        setEdges(rfEdges);
        // Open with the Start node centred at the top of the canvas (the
        // saved zoom is kept, capped at 100%); a flow without a start node
        // falls back to fitting everything.
        const initial = gr.initialNodeId == null ? null : rfNodes.find((n) => Number(n.id) === gr.initialNodeId);
        const wrapper = wrapperRef.current;
        if (initial && wrapper) {
          const zoom = Math.min(1, Math.max(0.2, layout?.viewport?.zoom ?? 1));
          // Start node sits 120px above the initial node, centred on its 200px width.
          const startCenterX = initial.position.x + 100;
          const startTopY = initial.position.y - 120;
          rf.setViewport({
            x: wrapper.clientWidth / 2 - startCenterX * zoom,
            y: 40 - startTopY * zoom,
            zoom,
          });
        } else {
          // Defer fitView so React Flow has measured the new nodes.
          // `maxZoom: 1` caps the auto-fit at 100% — without it, a flow
          // with a single (or very few) nodes would zoom in so far that
          // one card fills the canvas, which looks broken.
          setTimeout(() => rf.fitView({ padding: 0.2, maxZoom: 1 }), 50);
        }
      } catch (ex) {
        setError(`Failed to load flow: ${(ex as Error).message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, flowId]);

  // Refresh node data fields (label, parameters, isInitial flag) when the
  // user edits in the properties panel without rebuilding the whole node.
  const patchNodeData = useCallback(
    (nodeId: number, patch: Partial<ModuleNodeData>) => {
      setNodes((ns) =>
        ns.map((n) =>
          Number(n.id) === nodeId
            ? ({ ...n, data: { ...n.data, ...patch } } as Node<ModuleNodeData>)
            : n
        )
      );
    },
    []
  );

  // ---------- Layout autosave (debounced) ----------
  const layoutTimer = useRef<number | null>(null);
  const scheduleLayoutSave = useCallback(() => {
    if (readOnly) return;
    setSaved(false);
    if (layoutTimer.current) window.clearTimeout(layoutTimer.current);
    layoutTimer.current = window.setTimeout(async () => {
      setSaving(true);
      try {
        const positions: Record<string, NodePosition> = {};
        const inactiveExits: Record<string, number[]> = {};
        for (const n of rf.getNodes()) {
          if (n.id === START_NODE_ID) continue;
          positions[n.id] = { x: n.position.x, y: n.position.y };
          // The "Not used" toggle sets exitStates[exitId] = false. The
          // legacy NodeExit table has no isActive column, so we mirror
          // those falses into the layout doc here. Trues / unset values
          // fall through to ModuleExit.showByDefault and don't need to
          // be carried.
          const states = (n.data as ModuleNodeData).exitStates ?? {};
          const off = Object.entries(states)
            .filter(([, v]) => v === false)
            .map(([k]) => Number(k))
            .filter((id) => Number.isFinite(id));
          if (off.length > 0) inactiveExits[n.id] = off;
        }
        const doc: LayoutDocument = {
          version: 1,
          positions,
          viewport: rf.getViewport(),
          inactiveExits: Object.keys(inactiveExits).length > 0 ? inactiveExits : undefined,
        };
        await api.saveFlowLayout(applicationId, flowId, JSON.stringify(doc));
        setSaved(true);
      } catch (ex) {
        setError(`Layout save failed: ${(ex as Error).message}`);
      } finally {
        setSaving(false);
      }
    }, 600);
  }, [applicationId, flowId, rf, readOnly]);

  // ---------- React Flow change handlers ----------
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Drop any change that targets the synthetic Start node so it never
      // leaks into our persisted state.
      const real = changes.filter((c) => {
        const id = (c as { id?: string }).id;
        return id !== START_NODE_ID;
      });
      if (real.length === 0) return;
      setNodes((ns) => applyNodeChanges(real, ns) as Node<ModuleNodeData>[]);
      if (real.some((c) => c.type === 'position' && (c as { dragging?: boolean }).dragging === false)) {
        scheduleLayoutSave();
      }
    },
    [scheduleLayoutSave]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const real = changes.filter((c) => {
        const id = (c as { id?: string }).id;
        return id !== START_EDGE_ID;
      });
      if (real.length === 0) return;
      setEdges((es) => applyEdgeChanges(real, es));
    },
    []
  );

  // Selection — ignore the synthetic start node/edge. We model three panel
  // states:
  //   - single node selected              -> node properties
  //   - single edge selected (no nodes)   -> edge properties
  //   - anything else (0 or multi)        -> empty panel
  // Compact layout (≤1023px): the properties panel is a full-screen modal.
  // React Flow selects a node as soon as a drag starts, which would open
  // the modal over the canvas and kill a touch drag; so on the compact
  // layout the modal opens only on a real tap/click (onNodeClick /
  // onEdgeClick), never on the selection a drag start causes.
  const [isCompact, setIsCompact] = useState<boolean>(
    () => typeof window !== 'undefined' && (window.matchMedia?.('(max-width: 1023px)').matches ?? false),
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(max-width: 1023px)');
    if (!mq) return;
    const onChange = () => setIsCompact(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [propsOpenCompact, setPropsOpenCompact] = useState(false);
  const openPropsCompact = useCallback(() => setPropsOpenCompact(true), []);
  useEffect(() => {
    if (selectedNodeId == null && selectedEdgeId == null) setPropsOpenCompact(false);
  }, [selectedNodeId, selectedEdgeId]);

  /** Deselects everything (phones: closes the properties drawer). */
  const clearSelection = useCallback(() => {
    setPropsOpenCompact(false);
    setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n)));
    setEdges((es) => es.map((e) => (e.selected ? { ...e, selected: false } : e)));
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, []);

  const onSelectionChange = useCallback((p: { nodes: Node[]; edges: Edge[] }) => {
    const pickedNodes = p.nodes.filter((n) => n.id !== START_NODE_ID);
    const pickedEdges = p.edges.filter((e) => e.id !== START_EDGE_ID);
    if (pickedNodes.length === 1 && pickedEdges.length === 0) {
      setSelectedNodeId(Number(pickedNodes[0].id));
      setSelectedEdgeId(null);
    } else if (pickedEdges.length === 1 && pickedNodes.length === 0) {
      setSelectedNodeId(null);
      setSelectedEdgeId(pickedEdges[0].id);
    } else {
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
    }
  }, []);

  // ---------- Connect (create edge -> SetNodeExit) ----------
  const onConnect = useCallback(
    async (conn: Connection) => {
      if (readOnly) return;
      if (!conn.source || !conn.target || !conn.sourceHandle) return;
      const fromNodeId = Number(conn.source);
      const toNodeId = Number(conn.target);
      const handle = conn.sourceHandle;

      const exitId = Number(handle);
      const newEdgeId = `e_${fromNodeId}_${exitId}`;
      // Register the new edge for an intro bounce BEFORE adding it to
      // state. ElasticEdge's mount effect consumes the flag via the
      // module-level Set in ElasticEdge.tsx. We keep the new edge's
      // shape (id/source/target/sourceHandle/targetHandle/type) byte-
      // for-byte identical to the loaded edges produced by `hydrate`,
      // so React Flow's memo path is the same — passing a `data` or
      // `animated` flag on the new edge was enough to make subsequent
      // sourceX/sourceY updates silently get skipped.
      freshEdgeIds.add(newEdgeId);
      setEdges((es) => {
        const next = es.filter(
          (e) => !(Number(e.source) === fromNodeId && e.sourceHandle === conn.sourceHandle)
        );
        return addEdge(
          {
            ...conn,
            id: newEdgeId,
            type: 'elastic',
          },
          next
        );
      });
      setSaved(false);
      try {
        const res = await api.setNodeExit(applicationId, flowId, fromNodeId, exitId, toNodeId);
        if (!res.result) throw new Error('SetNodeExit returned false');
        setSaved(true);
      } catch (ex) {
        setError(`Connect failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, readOnly, scheduleLayoutSave]
  );

  // ---------- Reconnect (drag an existing edge endpoint to a new handle) ----------
  // React Flow renders endpoint hit zones within `reconnectRadius`
  // pixels of an edge's source/target. When the user grabs there and
  // drags, `onReconnectStart` fires; if they drop on a valid handle we
  // get `onReconnect(oldEdge, newConnection)`; otherwise only
  // `onReconnectEnd` fires. We use the latter (without a preceding
  // `onReconnect`) as the cue to "spring back" the original edge by
  // triggering its bounce.
  const reconnectAcceptedRef = useRef(false);

  const onReconnectStart = useCallback(() => {
    reconnectAcceptedRef.current = false;
  }, []);

  const onReconnect = useCallback(
    async (oldEdge: Edge, newConn: Connection) => {
      if (readOnly) return;
      if (!newConn.source || !newConn.target || !newConn.sourceHandle) return;
      reconnectAcceptedRef.current = true;
      const newFromNodeId = Number(newConn.source);
      const newToNodeId = Number(newConn.target);
      const newHandle = newConn.sourceHandle;
      const oldHandle = oldEdge.sourceHandle ?? '';

      const oldFromNodeId = Number(oldEdge.source);
      const oldExitId = Number(oldHandle);
      const newExitId = Number(newHandle);
      const newEdgeId = `e_${newFromNodeId}_${newExitId}`;
      // Replace the old edge with one that matches the new endpoint.
      // We also drop any other edge that was already on the new exit
      // (one connection per exit, same as `onConnect`).
      freshEdgeIds.add(newEdgeId);
      setEdges((es) => {
        const next = es.filter(
          (e) =>
            e.id !== oldEdge.id &&
            !(Number(e.source) === newFromNodeId && e.sourceHandle === newConn.sourceHandle),
        );
        return next.concat({
          id: newEdgeId,
          source: String(newFromNodeId),
          target: String(newToNodeId),
          sourceHandle: String(newExitId),
          targetHandle: newConn.targetHandle ?? 'in',
          type: 'elastic',
        });
      });
      setSaved(false);
      try {
        // If the source exit changed we clear the old one (its NextNodeId
        // would otherwise still point at the previous target).
        if (oldFromNodeId !== newFromNodeId || oldExitId !== newExitId) {
          await api.setNodeExit(applicationId, flowId, oldFromNodeId, oldExitId, null);
        }
        await api.setNodeExit(applicationId, flowId, newFromNodeId, newExitId, newToNodeId);
        setSaved(true);
      } catch (ex) {
        setError(`Reconnect failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, readOnly, scheduleLayoutSave],
  );

  const onReconnectEnd = useCallback(
    (
      _e: MouseEvent | TouchEvent,
      edge: Edge,
      handleType: 'source' | 'target',
      connectionState: { to?: { x: number; y: number } | null } | null,
    ) => {
      // Dropped on empty space → React Flow leaves the edge at its
      // original endpoints. We want a smooth, bouncing snap-back from
      // wherever the cursor was, so we pre-displace the grabbed end's
      // spring to the cursor offset and let the physics swing it home.
      if (reconnectAcceptedRef.current) return;
      const cursor = connectionState?.to ?? null;
      const node =
        handleType === 'source' ? rf.getNode(edge.source) : rf.getNode(edge.target);
      if (!cursor || !node) {
        // Fallback: plain bounce-in-place if we can't compute the offset.
        triggerEdgeBounce(edge.id);
        return;
      }
      // Approximate the grabbed endpoint's "home" position. Source
      // exits sit along the node's bottom edge, inputs at the top —
      // node-center horizontally + top/bottom vertically is close
      // enough to make the bounce feel anchored to the right side.
      const measured = (node as { measured?: { width?: number; height?: number } }).measured;
      const w = measured?.width ?? node.width ?? 200;
      const h = measured?.height ?? node.height ?? 100;
      const endpointPos =
        handleType === 'source'
          ? { x: node.position.x + w / 2, y: node.position.y + h }
          : { x: node.position.x + w / 2, y: node.position.y };
      triggerEdgeBounce(edge.id, {
        handleType,
        offsetX: cursor.x - endpointPos.x,
        offsetY: cursor.y - endpointPos.y,
      });
    },
    [rf],
  );

  // Edge delete via the React Flow keyboard/UI -> SetNodeExit with referenceNodeId=null.
  const onEdgesDelete = useCallback(
    async (toDelete: Edge[]) => {
      if (readOnly) return;
      setSaved(false);
      try {
        for (const e of toDelete) {
          if (!e.sourceHandle) continue;
          await api.setNodeExit(applicationId, flowId, Number(e.source), Number(e.sourceHandle), null);
        }
        setSaved(true);
      } catch (ex) {
        setError(`Disconnect failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, readOnly]
  );

  // Node delete via React Flow (key/menu) -> DeleteNode.
  const onNodesDelete = useCallback(
    async (toDelete: Node[]) => {
      if (readOnly) return;
      setSaved(false);
      try {
        for (const n of toDelete) {
          await api.deleteNode(applicationId, flowId, Number(n.id));
        }
        // Drop edges that referenced these nodes
        setEdges((es) =>
          es.filter(
            (e) =>
              !toDelete.some((n) => Number(e.source) === Number(n.id) || Number(e.target) === Number(n.id))
          )
        );
        scheduleLayoutSave();
        setSaved(true);
      } catch (ex) {
        setError(`Delete failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, scheduleLayoutSave, readOnly]
  );

  // ---------- Drag & drop from palette ----------
  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  /**
   * Adds a module node at a screen position (mouse drop and touch drop share
   * this). With `anchor: 'input'` the node's input handle (top centre) lands
   * on the point instead of the node's top-left corner.
   */
  const addModuleAt = useCallback(
    async (moduleId: number, clientX: number, clientY: number, anchor: 'topLeft' | 'input' = 'topLeft'): Promise<number | null> => {
      if (readOnly) return null;
      const def = moduleMap.get(moduleId);
      if (!def) return null;
      const wrapper = wrapperRef.current;
      if (!wrapper) return null;

      // screenToFlowPosition takes client coordinates and subtracts the
      // canvas offset itself; subtracting it here too put nodes off by the
      // width of the palette and the height of the bars.
      const position = rf.screenToFlowPosition({ x: clientX, y: clientY });
      if (anchor === 'input') position.x -= MODULE_NODE_WIDTH / 2;

      try {
        const res = await api.addNode(applicationId, flowId, moduleId, def.name);
        if (!res.result) throw new Error('AddNode returned false');
        const newNodeId = res.id;
        const data: ModuleNodeData = {
          nodeId: newNodeId,
          moduleId,
          label: def.name,
          moduleDef: def,
          parameters: {},
          exitStates: {},
          isInitial: false,
        };
        const newNode: Node<ModuleNodeData> = {
          id: String(newNodeId),
          type: 'module',
          position,
          data,
        };
        setNodes((ns) => ns.concat(newNode));
        scheduleLayoutSave();
        return newNodeId;
      } catch (ex) {
        setError(`AddNode failed: ${(ex as Error).message}`);
        return null;
      }
    },
    [applicationId, flowId, moduleMap, rf, scheduleLayoutSave, readOnly]
  );

  // ---------- Drop a connection on empty canvas → pick a module to create there ----------
  const [connectPicker, setConnectPicker] = useState<{
    fromNodeId: number;
    handle: string;
    clientX: number;
    clientY: number;
  } | null>(null);
  const closeConnectPicker = useCallback(() => setConnectPicker(null), []);

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, state: FinalConnectionState) => {
      if (readOnly) return;
      // A drop on a handle is handled by onConnect; only empty canvas opens the picker.
      if (state.isValid || state.toNode || !state.fromNode || !state.fromHandle) return;
      if (state.fromHandle.type !== 'source' || !state.fromHandle.id) return;
      if (state.fromNode.id === START_NODE_ID) return;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const point = 'changedTouches' in event ? event.changedTouches[0] : event;
      if (!point) return;
      const b = wrapper.getBoundingClientRect();
      if (point.clientX < b.left || point.clientX > b.right || point.clientY < b.top || point.clientY > b.bottom) return;
      setConnectPicker({
        fromNodeId: Number(state.fromNode.id),
        handle: state.fromHandle.id,
        clientX: point.clientX,
        clientY: point.clientY,
      });
    },
    [readOnly]
  );

  const onConnectPick = useCallback(
    async (moduleId: number) => {
      const p = connectPicker;
      setConnectPicker(null);
      if (!p) return;
      // The new node's input handle lands exactly where the line was dropped.
      const newId = await addModuleAt(moduleId, p.clientX, p.clientY, 'input');
      if (newId == null) return;
      await onConnect({ source: String(p.fromNodeId), sourceHandle: p.handle, target: String(newId), targetHandle: 'in' });
    },
    [connectPicker, addModuleAt, onConnect]
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      const moduleIdStr = e.dataTransfer.getData('application/x-flowbuilder-module');
      if (!moduleIdStr) return;
      void addModuleAt(Number(moduleIdStr), e.clientX, e.clientY);
    },
    [addModuleAt]
  );

  /** Touch drag from the palette: only a finger lifted over the canvas adds a node. */
  const onTouchDrop = useCallback(
    (moduleId: number, clientX: number, clientY: number) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const b = wrapper.getBoundingClientRect();
      if (clientX < b.left || clientX > b.right || clientY < b.top || clientY > b.bottom) return;
      void addModuleAt(moduleId, clientX, clientY);
      // On phones the palette is a drawer over the canvas: close it so the new node is visible.
      if (window.matchMedia?.('(max-width: 1023px)').matches) setPaletteCollapsed(true);
    },
    [addModuleAt]
  );

  // ---------- Properties panel callbacks ----------
  const selectedNodeData = useMemo<ModuleNodeData | null>(() => {
    if (selectedNodeId == null) return null;
    const n = nodes.find((x) => Number(x.id) === selectedNodeId);
    return n ? n.data : null;
  }, [selectedNodeId, nodes]);

  // Map nodeId -> label for the "Output ports" section in the properties
  // panel. Kept in sync with the current node set so renaming updates the
  // destination label live.
  const nodeNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const n of nodes) m.set(Number(n.id), n.data.label || `Node ${n.id}`);
    return m;
  }, [nodes]);

  // Resolves the currently-selected edge (if any) into a display object for
  // the properties panel: source node label + exit name, target node label.
  const selectedEdgeInfo = useMemo(() => {
    if (!selectedEdgeId) return null;
    const e = edges.find((x) => x.id === selectedEdgeId);
    if (!e) return null;
    const fromId = Number(e.source);
    const toId = Number(e.target);
    const exitId = Number(e.sourceHandle);
    const fromNode = nodes.find((n) => Number(n.id) === fromId);
    const exit = fromNode?.data.moduleDef?.exits.find((ex) => ex.exitId === exitId);
    return {
      edgeId: e.id,
      fromNodeId: fromId,
      fromNodeName: nodeNameById.get(fromId) || `Node ${fromId}`,
      fromModuleName: fromNode?.data.moduleDef?.name ?? null,
      exitId,
      exitName: exit?.name ?? `exit ${exitId}`,
      toNodeId: toId,
      toNodeName: nodeNameById.get(toId) || `Node ${toId}`,
    };
  }, [selectedEdgeId, edges, nodes, nodeNameById]);

  // ---------- Synthetic Start node ----------
  // Draw a decorative Start node above the InitialNodeId with an edge to it so
  // the entry point is obvious. Recomputed whenever the initial node or its
  // position changes.
  //
  // Also augments every real node's data with an `exitTargets` map (exitId ->
  // {nodeId, name}) so ModuleNode can render hover tooltips on its exit
  // handles without taking a dependency on the global edge state.
  const displayed = useMemo(() => {
    const exitTargetsByNode = new Map<number, Record<number, { nodeId: number; name: string }>>();
    for (const e of edges) {
      const fromId = Number(e.source);
      const toId = Number(e.target);
      if (!Number.isFinite(fromId) || !Number.isFinite(toId)) continue;
      const handle = e.sourceHandle ?? '';
      const exitId = Number(handle);
      if (!Number.isFinite(exitId)) continue;
      const map = exitTargetsByNode.get(fromId) ?? {};
      map[exitId] = { nodeId: toId, name: nodeNameById.get(toId) || `Node ${toId}` };
      exitTargetsByNode.set(fromId, map);
    }
    const augmentedNodes = nodes.map((n) => {
      const targets = exitTargetsByNode.get(Number(n.id));
      if (n.data.exitTargets === targets) return n;
      return {
        ...n,
        data: { ...n.data, exitTargets: targets },
      } as Node<ModuleNodeData>;
    });
    // Always render real edges through the elastic edge type, even if the
    // underlying state still says 'default' (e.g. after a hot reload that
    // preserved the previous edges-state). Idempotent — no-op for edges
    // that are already elastic.
    const renderedEdges = edges.map((e) =>
      e.type === 'elastic' ? e : ({ ...e, type: 'elastic' } as Edge),
    );
    if (initialNodeId == null) return { nodes: augmentedNodes as Node[], edges: renderedEdges };
    const target = augmentedNodes.find((n) => Number(n.id) === initialNodeId);
    if (!target) return { nodes: augmentedNodes as Node[], edges: renderedEdges };
    // Centre the 88px-wide Start node horizontally above the 200px-wide
    // initial node: (200 - 88) / 2 = 56.
    const startNode: Node = {
      id: START_NODE_ID,
      type: 'start',
      position: { x: target.position.x + 56, y: target.position.y - 120 },
      data: {},
      draggable: false,
      selectable: false,
      deletable: false,
      connectable: false,
    };
    const startEdge: Edge = {
      id: START_EDGE_ID,
      source: START_NODE_ID,
      sourceHandle: 'out',
      target: String(initialNodeId),
      targetHandle: 'in',
      type: 'default',
      animated: true,
      style: { stroke: '#22c55e', strokeWidth: 2 },
      deletable: false,
      selectable: false,
    };
    return {
      nodes: [startNode, ...augmentedNodes] as Node[],
      edges: [startEdge, ...renderedEdges],
    };
  }, [nodes, edges, initialNodeId, nodeNameById]);

  const onChangeName = useCallback(
    async (name: string) => {
      if (readOnly) return;
      if (selectedNodeId == null || !selectedNodeData) return;
      patchNodeData(selectedNodeId, { label: name });
      setSaved(false);
      try {
        const params = collectParams(selectedNodeData, selectedNodeData.moduleDef);
        await api.updateNode(applicationId, flowId, selectedNodeId, name, params);
        setSaved(true);
      } catch (ex) {
        setError(`Rename failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, patchNodeData, selectedNodeId, selectedNodeData, readOnly]
  );

  const onChangeParam = useCallback(
    async (param: ModuleParameter, value: string, kind: string) => {
      if (readOnly) return;
      if (selectedNodeId == null || !selectedNodeData) return;
      const newParams = {
        ...selectedNodeData.parameters,
        [param.parameterId]: {
          parameterId: param.parameterId,
          value: value,
          valueType: kind,
        },
      };
      patchNodeData(selectedNodeId, { parameters: newParams });
      setSaved(false);
      try {
        const merged: ModuleNodeData = { ...selectedNodeData, parameters: newParams };
        const params = collectParams(merged, selectedNodeData.moduleDef);
        await api.updateNode(applicationId, flowId, selectedNodeId, selectedNodeData.label, params);
        setSaved(true);
      } catch (ex) {
        setError(`Param save failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, patchNodeData, selectedNodeId, selectedNodeData, readOnly]
  );

  // Flip a single exit's isActive flag on the currently-selected node. The
  // server persists immediately (idempotent POST). If the exit was connected
  // and we're deactivating it, the caller already confirmed with the user;
  // we drop the edge locally to keep the canvas consistent with the server
  // (the NodeExit row stays, but we zero out the NextNodeId via SetNodeExit
  // so reopening the flow doesn't resurrect a ghost edge).
  const onSetExitActive = useCallback(
    async (exit: ModuleExit, isActive: boolean, isConnected: boolean) => {
      if (readOnly) return;
      if (selectedNodeId == null || !selectedNodeData) return;
      if (!isActive && isConnected) {
        const ok = await confirmAsync(
          'Deactivate exit?',
          `Exit "${exit.name}" is connected. Deactivating will hide the handle and remove the connection.`,
          { tone: 'caution', confirmLabel: 'Deactivate' },
        );
        if (!ok) return;
      }
      const nextStates = { ...(selectedNodeData.exitStates ?? {}) };
      nextStates[exit.exitId] = isActive;
      patchNodeData(selectedNodeId, { exitStates: nextStates });
      if (!isActive && isConnected) {
        setEdges((es) =>
          es.filter(
            (e) =>
              !(Number(e.source) === selectedNodeId && Number(e.sourceHandle) === exit.exitId)
          )
        );
      }
      setSaved(false);
      try {
        if (!isActive && isConnected) {
          await api.setNodeExit(applicationId, flowId, selectedNodeId, exit.exitId, null);
        }
        // Persist the visibility flip alongside positions/viewport — the
        // legacy NodeExit table has no isActive column so the layout doc
        // is our only durable home for this state.
        scheduleLayoutSave();
        // The server-side call is a near no-op when the schema is missing
        // the column, but we keep it so a future schema upgrade picks up
        // existing UI state automatically.
        await api.setNodeExitActive(applicationId, flowId, selectedNodeId, exit.exitId, isActive);
        setSaved(true);
      } catch (ex) {
        setError(`${isActive ? 'Activate' : 'Deactivate'} failed: ${(ex as Error).message}`);
      }
    },
    [applicationId, flowId, patchNodeData, scheduleLayoutSave, selectedNodeId, selectedNodeData, readOnly, confirmAsync]
  );

  // Counts real (non-synthetic) edges that touch `nodeId` (either as
  // source or target).
  const edgeCountForNode = useCallback(
    (nodeId: number) =>
      edges.filter(
        (e) =>
          e.id !== START_EDGE_ID &&
          (Number(e.source) === nodeId || Number(e.target) === nodeId)
      ).length,
    [edges]
  );

  // React Flow v12 fires `onBeforeDelete` before nodes/edges actually go
  // away (from DEL/Backspace or the Delete button in our panel). We use it
  // to prompt the user when deleting a node that still has connections.
  // Returning `false` cancels the entire operation; returning the (possibly
  // reduced) set allows it to proceed.
  const onBeforeDelete = useCallback(
    async ({ nodes: toDeleteNodes, edges: toDeleteEdges }: { nodes: Node[]; edges: Edge[] }) => {
      if (readOnly) return false;
      const realNodes = toDeleteNodes.filter((n) => n.id !== START_NODE_ID);
      const realEdges = toDeleteEdges.filter((e) => e.id !== START_EDGE_ID);
      if (realNodes.length === 0 && realEdges.length === 0) return false;
      const nodesWithEdges = realNodes.filter((n) => edgeCountForNode(Number(n.id)) > 0);
      if (nodesWithEdges.length > 0) {
        const names = nodesWithEdges
          .map((n) => `"${(n.data as ModuleNodeData).label}"`)
          .join(', ');
        const msg = `${names} still ${nodesWithEdges.length === 1 ? 'has' : 'have'} connections. Delete and remove all attached connections?`;
        if (!(await confirmAsync('Delete from canvas?', msg, { confirmLabel: 'Delete all' }))) return false;
      }
      return { nodes: realNodes, edges: realEdges };
    },
    [edgeCountForNode, readOnly, confirmAsync]
  );

  // Deletes a single node from the UI after user confirmation (when it has
  // connections). Attached edges are removed server-side by onEdgesDelete /
  // cascade inside api.deleteNode, and locally by onNodesDelete.
  const confirmAndDeleteNode = useCallback(
    async (nodeId: number) => {
      if (readOnly) return;
      const n = nodes.find((x) => Number(x.id) === nodeId);
      if (!n) return;
      const count = edgeCountForNode(nodeId);
      if (count > 0) {
        const plural = count === 1 ? 'connection' : 'connections';
        const msg = `"${n.data.label}" has ${count} ${plural}. Delete the node and remove all attached connections?`;
        if (!(await confirmAsync('Delete node?', msg))) return;
      }
      setNodes((ns) => ns.filter((x) => x !== n));
      setEdges((es) =>
        es.filter((e) => Number(e.source) !== nodeId && Number(e.target) !== nodeId)
      );
      onNodesDelete([n]);
      if (selectedNodeId === nodeId) setSelectedNodeId(null);
    },
    [nodes, edgeCountForNode, onNodesDelete, readOnly, selectedNodeId, confirmAsync]
  );

  const confirmAndDeleteEdge = useCallback(
    (edgeId: string) => {
      if (readOnly) return;
      const e = edges.find((x) => x.id === edgeId);
      if (!e) return;
      setEdges((es) => es.filter((x) => x.id !== edgeId));
      onEdgesDelete([e]);
      if (selectedEdgeId === edgeId) setSelectedEdgeId(null);
    },
    [edges, onEdgesDelete, readOnly, selectedEdgeId]
  );

  const onDeleteSelected = useCallback(() => {
    if (selectedNodeId != null) {
      confirmAndDeleteNode(selectedNodeId);
    } else if (selectedEdgeId != null) {
      confirmAndDeleteEdge(selectedEdgeId);
    }
  }, [confirmAndDeleteNode, confirmAndDeleteEdge, selectedNodeId, selectedEdgeId]);

  const onSetInitial = useCallback(async () => {
    if (readOnly) return;
    if (selectedNodeId == null || !graph) return;
    setInitialNodeId(selectedNodeId);
    setNodes((ns) =>
      ns.map(
        (n) =>
          ({ ...n, data: { ...n.data, isInitial: Number(n.id) === selectedNodeId } }) as Node<ModuleNodeData>
      )
    );
    setSaved(false);
    try {
      await api.updateFlow({
        customerId: graph.customerId,
        applicationId,
        flowId,
        flowName,
        initialNodeId: selectedNodeId,
      });
      setSaved(true);
    } catch (ex) {
      setError(`UpdateFlow failed: ${(ex as Error).message}`);
    }
  }, [applicationId, flowId, flowName, graph, selectedNodeId, readOnly]);

  // ---------- Topbar actions ----------
  // `maxZoom: 1` caps the auto-fit at 100% so a tiny flow (one or two
  // nodes) doesn't render as one giant card filling the canvas.
  const onFit = () => rf.fitView({ padding: 0.2, maxZoom: 1 });

  const onReorder = useCallback(() => {
    if (readOnly) return;
    setNodes((curr) =>
      autoLayoutTopDown(curr, edges, {
        startNodeId: initialNodeId != null ? String(initialNodeId) : undefined,
      }),
    );
    setSaved(false);
    // Wait one tick so the new node positions have committed before
    // we ask React Flow to refit — otherwise it fits against the
    // pre-layout positions.
    setTimeout(() => rf.fitView({ padding: 0.2, maxZoom: 1 }), 50);
  }, [readOnly, edges, initialNodeId, rf]);

  const onValidate = useCallback(async () => {
    const errs: string[] = [];
    if (!initialNodeId) errs.push('No start node selected');
    for (const n of nodes) {
      const def = n.data.moduleDef;
      if (!def) continue;
      for (const p of def.parameters) {
        if (p.allowNull) continue;
        const v = n.data.parameters[p.parameterId]?.value;
        if (v == null || v === '') {
          errs.push(`"${n.data.label}": parameter "${p.name}" is required`);
        }
      }
      for (const exit of def.exits) {
        const connected = edges.some(
          (e) => Number(e.source) === n.data.nodeId && e.sourceHandle === String(exit.exitId)
        );
        if (!connected) {
          errs.push(`"${n.data.label}": exit "${exit.name}" is unconnected`);
        }
      }
    }
    if (errs.length === 0) {
      await messageAsync('Validation OK', `OK — ${nodes.length} nodes, ${edges.length} connections`);
    } else {
      await messageAsync('Validation issues', 'Validation issues:\n\n' + errs.map((e) => '• ' + e).join('\n'));
    }
  }, [nodes, edges, initialNodeId, messageAsync]);

  const onPublish = useCallback(async () => {
    if (readOnly) return;
    if (
      !(await confirmAsync(
        'Publish draft?',
        'Publish this draft? The current saved version will be replaced.',
        { tone: 'caution', confirmLabel: 'Publish' },
      ))
    )
      return;
    setSaving(true);
    try {
      const res = await api.commitFlow(applicationId, flowId, false);
      if (!res.result) throw new Error('FlowCommit returned false');
      await messageAsync('Published', 'Published.');
    } catch (ex) {
      setError(`Publish failed: ${(ex as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [applicationId, flowId, readOnly, confirmAsync, messageAsync]);

  const onDiscard = useCallback(async () => {
    if (readOnly) return;
    if (
      !(await confirmAsync(
        'Discard draft?',
        'Discard this draft? All unsaved changes will be lost.',
        { tone: 'danger', confirmLabel: 'Discard' },
      ))
    )
      return;
    setSaving(true);
    try {
      const res = await api.discardFlow(applicationId, flowId);
      if (!res.result) throw new Error('FlowDiscard returned false');
      window.location.reload();
    } catch (ex) {
      setError(`Discard failed: ${(ex as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [applicationId, flowId, readOnly, confirmAsync]);

  // Phase 1: there is no claim/commit lifecycle, so "Edit / Claim" never
  // surfaces. Kept as a no-op here so the topbar prop wiring stays the same.
  const onEnterDraft = useCallback(() => {
    /* no-op in phase 1 */
  }, []);

  // Defer to the parent (FlowDesignerPage) to navigate back via React
  // Router; fall back to the previous full-page redirect if no callback.
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    window.history.back();
  }, [onBack]);

  const onExport = () => {
    const doc = {
      flowId,
      applicationId,
      name: flowName,
      initialNodeId,
      nodes: nodes.map((n) => ({
        id: Number(n.id),
        moduleId: n.data.moduleId,
        moduleName: n.data.moduleDef?.name ?? null,
        label: n.data.label,
        position: n.position,
        parameters: n.data.parameters,
      })),
      edges: edges.map((e) => ({
        from: Number(e.source),
        exitId: Number(e.sourceHandle),
        to: Number(e.target),
      })),
    };
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${flowName || 'flow'}.json`;
    a.click();
  };

  return (
    <div className={`fb-app${readOnly ? ' fb-app--readonly' : ''}`}>
      <Topbar
        flowName={flowName}
        saved={saved}
        saving={saving}
        readOnly={readOnly}
        stateLabel={readOnly ? 'Read-only' : 'Active'}
        canEnterDraft={false}
        showCommitControls={false}
        onFit={onFit}
        onReorder={onReorder}
        onValidate={onValidate}
        onExport={onExport}
        onPublish={onPublish}
        onDiscard={onDiscard}
        onEnterDraft={onEnterDraft}
        onBack={handleBack}
      />
      {error && (
        <div className="fb-error" onClick={() => setError(null)}>
          {error} <span className="fb-error-close">×</span>
        </div>
      )}
      <div className="fb-body">
        <Palette
          catalog={offeredCatalog}
          disabled={readOnly}
          collapsed={paletteCollapsed}
          onToggleCollapsed={togglePalette}
          onTouchDrop={onTouchDrop}
        />
        <div className="fb-canvas" ref={wrapperRef} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={displayed.nodes}
            edges={displayed.edges}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onSelectionChange={onSelectionChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onNodeClick={openPropsCompact}
            onEdgeClick={openPropsCompact}
            onReconnectStart={onReconnectStart}
            onReconnect={onReconnect}
            onReconnectEnd={onReconnectEnd}
            edgesReconnectable={!readOnly}
            reconnectRadius={14}
            onEdgesDelete={onEdgesDelete}
            onNodesDelete={onNodesDelete}
            onBeforeDelete={onBeforeDelete}
            defaultEdgeOptions={{ type: 'elastic' }}
            connectionLineComponent={ElasticConnectionLine}
            deleteKeyCode={['Delete', 'Backspace']}
            fitView={false}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            edgesFocusable={!readOnly}
            elementsSelectable
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} />
            <Controls />
            {miniMapVisible && (
              <MiniMap
                pannable
                zoomable
                bgColor="rgba(255, 255, 255, 0.9)"
                maskColor="rgba(15, 23, 42, 0.28)"
                nodeColor="#cbd5e1"
                nodeStrokeColor="#94a3b8"
                style={{ width: miniMapWidth, height: miniMapHeight }}
              />
            )}
          </ReactFlow>
          <button
            type="button"
            className={`fb-minimap-toggle${miniMapVisible ? ' fb-minimap-toggle--on' : ''}`}
            onClick={toggleMiniMap}
            title={miniMapVisible ? 'Hide map' : 'Show map'}
            aria-label={miniMapVisible ? 'Hide map' : 'Show map'}
            aria-pressed={miniMapVisible}
            style={miniMapVisible ? { bottom: 15 + miniMapHeight - 12 } : undefined}
          >
            {miniMapVisible ? '×' : 'Map'}
          </button>
          {connectPicker && (
            <ConnectPicker
              catalog={offeredCatalog}
              x={connectPicker.clientX}
              y={connectPicker.clientY}
              onPick={(id) => void onConnectPick(id)}
              onClose={closeConnectPicker}
            />
          )}
        </div>
        <div
          className="fb-splitter"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize properties panel"
          onMouseDown={onSplitterMouseDown}
        />
        <div
          className={`fb-props-host${
            selectedNodeData == null && selectedEdgeInfo == null || (isCompact && !propsOpenCompact) ? ' fb-props-host--empty' : ''
          }`}
          style={{ width: propsWidth }}
        >
          <button
            type="button"
            className="fb-props-close"
            aria-label="Close properties"
            title="Close"
            onClick={clearSelection}
          >
            ×
          </button>
          <PropertiesPanel
            selected={selectedNodeData}
            selectedEdge={selectedEdgeInfo}
            edges={mapEdgesForPanel(edges)}
            nodeNameById={nodeNameById}
            applicationId={applicationId}
            readOnly={readOnly}
            isInitialNode={selectedNodeId != null && selectedNodeId === initialNodeId}
            onChangeName={onChangeName}
            onChangeParam={onChangeParam}
            onSetExitActive={onSetExitActive}
            onDelete={onDeleteSelected}
            onSetInitial={onSetInitial}
          />
        </div>
      </div>

      {pendingDialog?.kind === 'confirm' && (
        <ConfirmModal
          open
          onClose={() => dismissConfirm(false)}
          title={pendingDialog.title}
          confirmLabel={
            pendingDialog.confirmLabel ??
            (pendingDialog.tone === 'caution' ? 'Continue' : 'Delete')
          }
          tone={pendingDialog.tone}
          onConfirm={() => dismissConfirm(true)}
        >
          {pendingDialog.message}
        </ConfirmModal>
      )}
      {pendingDialog?.kind === 'message' && (
        <MessageModal
          open
          onClose={dismissMessage}
          title={pendingDialog.title}
        >
          {pendingDialog.message}
        </MessageModal>
      )}
    </div>
  );
}

// ---------- helpers ----------

function parseLayout(json: string | null): LayoutDocument | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as LayoutDocument;
    if (parsed && parsed.version === 1 && parsed.positions) return parsed;
  } catch {
    // Ignore corrupt blobs - we just fall back to auto-layout.
  }
  return null;
}

function hydrate(graph: FlowGraph, catalog: ModuleCatalog, layout: LayoutDocument | null) {
  const moduleMap = new Map<number, ModuleDef>();
  for (const m of catalog.modules) moduleMap.set(m.moduleId, m);

  const inactiveExitMap = layout?.inactiveExits ?? {};
  // Build a quick set of (nodeId, exitId) pairs that the user has hidden
  // server-side connection state takes precedence so we never accidentally
  // hide a connected exit and orphan its edge.
  const connectedExits = new Set<string>();
  for (const e of graph.edges) connectedExits.add(`${e.fromNodeId}:${e.exitId}`);

  const rfNodes: Node<ModuleNodeData>[] = graph.nodes.map((n, i) => {
    const def = moduleMap.get(n.moduleId);
    const params: Record<number, FlowGraphNodeParamValue> = {};
    for (const p of n.parameters) params[p.parameterId] = p;
    // Stored NodeExit rows drive the per-node exit visibility map. Missing
    // entries fall back to ModuleExit.showByDefault at render time. Layout
    // doc overrides apply on top so a user-hidden ("Not used") exit stays
    // hidden across reloads.
    const exitStates: Record<number, boolean> = {};
    for (const e of n.exits ?? []) exitStates[e.exitId] = e.isActive;
    const inactive = inactiveExitMap[String(n.nodeId)] ?? [];
    for (const exitId of inactive) {
      if (connectedExits.has(`${n.nodeId}:${exitId}`)) continue;
      exitStates[exitId] = false;
    }
    const pos = layout?.positions?.[String(n.nodeId)] ?? autoPosition(i);
    const data: ModuleNodeData = {
      nodeId: n.nodeId,
      moduleId: n.moduleId,
      label: n.name || def?.name || `Node ${n.nodeId}`,
      moduleDef: def,
      parameters: params,
      exitStates,
      isInitial: graph.initialNodeId === n.nodeId,
    };
    return {
      id: String(n.nodeId),
      type: 'module',
      position: pos,
      data,
    };
  });

  const rfEdges: Edge[] = graph.edges.map((e) => ({
    id: `e_${e.fromNodeId}_${e.exitId}`,
    source: String(e.fromNodeId),
    target: String(e.toNodeId),
    sourceHandle: String(e.exitId),
    targetHandle: 'in',
    // Custom "elastic" edge: a spring-damped bezier whose curve trails
    // briefly behind moving endpoints and snaps in with a small bounce
    // when first drawn.
    type: 'elastic',
  }));

  // Per-intent edges (NodeExit rows keyed by IntentKey) are no longer
  // rendered: Mod_VB_Ask always leaves through SUCCESS and the flow branches
  // on the intent variable with a Condition node.

  return { rfNodes, rfEdges };
}

type FlowGraphNodeParamValue = { parameterId: number; value: string | null; valueType: string | null };

// Simple grid fallback when we have no saved positions.
function autoPosition(i: number): NodePosition {
  const cols = 4;
  return {
    x: 60 + (i % cols) * 260,
    y: 60 + Math.floor(i / cols) * 180,
  };
}

// The server persists each NodeParameter as `<kind>:<value>` where kind comes
// from `valueType`. We read the kind from the parameter if set, and otherwise
// fall back to the default kind for that TypeId (e.g. "string" for TypeId 1,
// "huntgroup" for TypeId 3, "voicefile" for TypeId 9, ...).
function collectParams(data: ModuleNodeData, def: ModuleDef | undefined) {
  if (!def) return [];
  return def.parameters.map((p) => {
    const cur = data.parameters[p.parameterId];
    const kind = cur?.valueType || defaultKindFor(p.typeId, p.typeName);
    return {
      id: p.parameterId,
      typeName: kind,
      value: cur?.value ?? p.defaultValue ?? '',
    };
  });
}

function mapEdgesForPanel(edges: Edge[]): FlowGraphEdge[] {
  return edges.map((e) => ({
    fromNodeId: Number(e.source),
    exitId: Number(e.sourceHandle),
    toNodeId: Number(e.target),
  }));
}
